import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  Paper,
  TextField,
  InputAdornment,
  Fab,
  Chip,
  ListItem,
  ListItemText,
  IconButton,
  ListItemSecondaryAction,
} from '@mui/material';

import {
  AddOutlined,
  SearchOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  EditOutlined,
  DeleteOutlined,
  ReceiptOutlined,
} from '@mui/icons-material';
import { useQuoteStore } from '../stores/useQuoteStore';
import { useClientStore } from '../stores/useClientStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useFactureStore } from '../stores/useFactureStore';
import { StatutDevis } from '../types';
import { formatEuro, formatDate } from '../utils/calculations';
import devisService from '../services/devisService';
import { useNotification } from '../contexts/NotificationContext';

const DevisListPage: React.FC = () => {
  const navigate = useNavigate();
  const { devis, loadDevis, deleteDevis, isLoading } = useQuoteStore();
  const { clients, loadClients } = useClientStore();
  const { getFacturesByDevisId } = useFactureStore();
  const { user } = useAuthStore();
  const { showConfirm, showSuccess, showError } = useNotification();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadDevis();
    loadClients();
  }, [loadDevis, loadClients]);

  const filteredDevis = devis.filter((d) => {
    const search = searchQuery.toLowerCase();
    const client = clients.find((c) => c.id === d.clientId);
    return (
      d.numero.toLowerCase().includes(search) ||
      client?.nom.toLowerCase().includes(search) ||
      client?.prenom.toLowerCase().includes(search)
    );
  });

  const getStatutColor = (statut: StatutDevis) => {
    switch (statut) {
      case StatutDevis.BROUILLON:
        return 'default';
      case StatutDevis.ENVOYE:
        return 'primary';
      case StatutDevis.ACCEPTE:
        return 'success';
      case StatutDevis.REFUSE:
        return 'error';
      default:
        return 'default';
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await showConfirm({
      title: 'Supprimer le devis',
      message: 'Êtes-vous sûr de vouloir supprimer ce devis ? Cette action est irréversible.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error',
    });

    if (confirmed) {
      deleteDevis(id);
      showSuccess('Devis supprimé avec succès');
    }
  };

  const handleGeneratePDF = async (devisId: number) => {
    const currentDevis = devis.find((d) => d.id === devisId);
    if (!currentDevis) return;

    try {
      // Télécharger le PDF depuis le backend
      const pdfBlob = await devisService.downloadPDF(devisId);

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Devis_${currentDevis.numero}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      showError('Erreur lors du téléchargement du PDF. Veuillez réessayer.');
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 700, color: '#333', mb: 3 }}
      >
        Devis
      </Typography>

      {/* Barre de recherche */}
      <TextField
        fullWidth
        placeholder="Rechercher un devis..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined />
            </InputAdornment>
          ),
          sx: { fontSize: '1.1rem' },
        }}
      />

      {/* Liste des devis */}
      {!isLoading && filteredDevis.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <DescriptionOutlined sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchQuery ? 'Aucun devis trouvé' : 'Aucun devis créé'}
          </Typography>
          {!searchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Commencez par créer votre premier devis
            </Typography>
          )}
        </Paper>
      ) : (
        <List sx={{ bgcolor: 'background.paper' }}>
          {filteredDevis.map((d) => {
            const client = clients.find((c) => c.id === d.clientId);

            // Utiliser le champ estFacture du backend si disponible
            // Sinon, fallback sur le calcul côté frontend (pour compatibilité)
            const devisCompletementFacture = d.estFacture ?? (() => {
              const factures = getFacturesByDevisId(d.id);
              return factures.some(f => f.type === 'COMPLETE' || f.type === 'SOLDE');
            })();

            return (
              <Paper key={d.id} sx={{ mb: 1 }}>
                <ListItem
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                  }}
                  onClick={() => navigate(`/devis/${d.id}`)}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {d.numero}
                        </Typography>
                        <Chip
                          label={d.statut}
                          color={getStatutColor(d.statut)}
                          size="small"
                        />
                        {devisCompletementFacture && (
                          <Chip
                            icon={<ReceiptOutlined sx={{ fontSize: '0.9rem' }} />}
                            label="Facturé"
                            size="small"
                            sx={{
                              bgcolor: '#F59E0B',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span">
                          👤 {client ? `${client.prenom} ${client.nom}` : 'Client inconnu'}
                        </Typography>
                        <br />
                        <Typography variant="body2" component="span">
                          📅 {formatDate(d.dateEmission || d.dateCreation || "")}
                        </Typography>
                        <br />
                        <Typography
                          variant="body1"
                          component="span"
                          sx={{ fontWeight: 600, color: '#4CAF50' }}
                        >
                          {formatEuro(d.totalTTC)}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGeneratePDF(d.id);
                      }}
                      sx={{ mr: 1 }}
                      color="primary"
                    >
                      <PictureAsPdfOutlined />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/devis/edit/${d.id}`);
                      }}
                      sx={{ mr: 1 }}
                    >
                      <EditOutlined />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(d.id);
                      }}
                      color="error"
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            );
          })}
        </List>
      )}

      {/* Bouton flottant pour ajouter */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 90,
          right: 20,
          background: 'linear-gradient(135deg, theme.palette.primary.main 0%, ${theme.palette.secondary.main} 100%)',
        }}
        onClick={() => navigate('/devis/new')}
      >
        <AddOutlined />
      </Fab>
    </Box>
  );
};

export default DevisListPage;
